import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { MYCARSLOT_OPEN_CHAT_EVENT } from '../../utils/openChat';
import '../AllCss/ChatBox.css';

const HUMAN_SUPPORT_QUESTIONS = [
  { key: 'customerName', text: 'What is your Full name?', required: true },
  { key: 'customerEmail', text: 'What is your email address?', required: true },
  { key: 'customerPhone', text: 'What is your phone number? (Optional)', required: false },
  { key: 'concern', text: 'What can we help you with?', required: true },
];

function ChatMessageText({ text }) {
  const lines = String(text || '').split('\n');
  return (
    <div className="chat-message-text">
      {lines.map((line, i) => (
        <span key={i}>
          {line}
          {i < lines.length - 1 ? <br /> : null}
        </span>
      ))}
    </div>
  );
}

function ChatBox() {
  const toast = useToast();
  const [chatVisible, setChatVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showInitialForm, setShowInitialForm] = useState(false); // Start with false - only show when requesting human
  const [initialInfoSubmitted, setInitialInfoSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    concern: '',
  });
  const [currentQuestion, setCurrentQuestion] = useState(0); // 0: name, 1: email, 2: phone, 3: concern
  const [formErrors, setFormErrors] = useState({});
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const navigate = useNavigate();
  const [requestingHuman, setRequestingHuman] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setChatVisible(true), 4000);
    return () => clearTimeout(t);
  }, []);

  // Generate or retrieve session ID
  useEffect(() => {
    let session = localStorage.getItem('chatSessionId');
    const submitted = localStorage.getItem('chatInitialInfoSubmitted');
    
    if (!session) {
      session = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chatSessionId', session);
    }
    
    setSessionId(session);
    
    // If user is logged in, pre-fill form
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        customerName: user.fullName || '',
        customerEmail: user.email || '',
        customerPhone: user.phone || '',
      }));
      // Skip to concern question if user is logged in
      if (user.fullName && user.email) {
        setCurrentQuestion(0); // Start with concern question
      }
    }
    
    // Check if initial info was already submitted
    if (submitted === 'true') {
      setInitialInfoSubmitted(true);
      setShowInitialForm(false);
    } else if (!submitted && messages.length === 0) {
      // Initialize with first bot message only if messages are empty
      setMessages([{
        id: 'init',
        message: HUMAN_SUPPORT_QUESTIONS[0].text,
        senderType: 'admin',
        senderName: 'Support',
        createdAt: new Date().toISOString(),
      }]);
      // Scroll to bottom after message is added
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
    // Intentionally excludes messages.length: avoid re-seeding chat when history loads
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount + auth; HUMAN_SUPPORT_QUESTIONS are module-level
  }, [isAuthenticated, user]);

  // Scroll to bottom when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      // Force scroll to bottom immediately
      const scrollToBottomNow = () => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      };
      
      // Multiple attempts to ensure it scrolls
      scrollToBottomNow();
      setTimeout(scrollToBottomNow, 50);
      setTimeout(scrollToBottomNow, 150);
      setTimeout(scrollToBottomNow, 300);
      setTimeout(scrollToBottomNow, 500);
    }
  }, [isOpen, isMinimized]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isOpen && !isMinimized && messages.length > 0) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    }
  }, [messages, isOpen, isMinimized]);

  // Load chat history when opened (no need to wait for initial info)
  useEffect(() => {
    if (isOpen && sessionId) {
      loadChatHistory();
      // Poll for new messages every 3 seconds
      const interval = setInterval(loadChatHistory, 3000);
      return () => clearInterval(interval);
    }
  }, [isOpen, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset requestingHuman when admin responds
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.senderType === 'admin' && 
          lastMessage.senderName !== 'AI Assistant' && 
          lastMessage.senderName !== 'System' && 
          lastMessage.senderName !== 'Support') {
        setRequestingHuman(false);
      }
    }
  }, [messages]);

  const loadChatHistory = async () => {
    try {
      const params = isAuthenticated && user
        ? { userId: user.id }
        : { sessionId };

      const response = await axios.get(`${API_URL}/api/chat/history`, {
        withCredentials: true, params });
      
      if (response.data.success) {
        // Parse metadata for each message and ensure unique IDs
        const parsedMessages = (response.data.data.chats || []).map(msg => ({
          ...msg,
          id: msg.id || `msg-${Date.now()}-${Math.random()}`,
          metadata: msg.metadata ? (typeof msg.metadata === 'string' ? JSON.parse(msg.metadata) : msg.metadata) : null,
          actions: msg.metadata?.actions || [],
        }));
        
        // Remove duplicates based on ID
        const uniqueMessages = parsedMessages.reduce((acc, current) => {
          const existing = acc.find(msg => msg.id === current.id);
          if (!existing) {
            acc.push(current);
          }
          return acc;
        }, []);
        
        setMessages(uniqueMessages);
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const validateAnswer = (key, value) => {
    if (key === 'customerName' && !value.trim()) {
      return 'Please provide your full name';
    }
    if (key === 'customerEmail') {
      if (!value.trim()) {
        return 'Please provide your email address';
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Please enter a valid email address';
      }
    }
    if (key === 'concern' && !value.trim()) {
      return 'Please tell us how we can help you';
    }
    return null;
  };

  const handleAnswerSubmit = async (e) => {
    e.preventDefault();
    const currentQ = HUMAN_SUPPORT_QUESTIONS[currentQuestion];
    const answer = newMessage.trim();
    
    // Allow empty answer for optional questions
    if (!answer && currentQ.required) {
      const error = validateAnswer(currentQ.key, answer);
      if (error) {
        setFormErrors({ [currentQ.key]: error });
        return;
      }
    }

    // Clear error
    setFormErrors({});

    // Add user message (even if empty for optional fields)
    if (answer) {
      const userMessage = {
        id: `user-${Date.now()}`,
        message: answer,
        senderType: 'user',
        senderName: 'You',
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMessage]);
    }

    // Update form data
    setFormData(prev => ({ ...prev, [currentQ.key]: answer }));
    setNewMessage('');

    // Move to next question or submit
    if (currentQuestion < HUMAN_SUPPORT_QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      // Add next bot question
      setTimeout(() => {
        const nextQ = HUMAN_SUPPORT_QUESTIONS[currentQuestion + 1];
        const botMessage = {
          id: `bot-${Date.now()}`,
          message: nextQ.text,
          senderType: 'admin',
          senderName: 'Support',
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, botMessage]);
        scrollToBottom();
      }, 500);
    } else {
      // All questions answered, submit
      await submitAllInfo();
    }
  };

  const submitAllInfo = async () => {
    setSubmittingForm(true);
    try {
      const response = await axios.post(`${API_URL}/api/chat/initial-info`, {
        withCredentials: true,
        sessionId,
        customerName: formData.customerName.trim(),
        customerEmail: formData.customerEmail.trim(),
        customerPhone: formData.customerPhone.trim() || null,
        concern: formData.concern.trim(),
        userId: isAuthenticated && user ? user.id : null,
      });

      if (response.data.success) {
        setInitialInfoSubmitted(true);
        setShowInitialForm(false);
        localStorage.setItem('chatInitialInfoSubmitted', 'true');
        
        // Don't add any system message - keep conversation clean
        // Just load chat history to show any existing messages
        await loadChatHistory();
      }
    } catch (error) {
      console.error('Error submitting initial info:', error);
      const errorMessage = {
        id: `bot-error-${Date.now()}`,
        message: 'Sorry, there was an error. Please try again.',
        senderType: 'admin',
        senderName: 'Support',
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setSubmittingForm(false);
    }
  };


  const scrollToBottom = (instant = false) => {
    setTimeout(() => {
      // Try scrolling the container directly first
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
      // Also use scrollIntoView as fallback
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: instant ? 'auto' : 'smooth',
          block: 'end'
        });
      }
    }, instant ? 50 : 150);
  };

  const handleOpenChat = useCallback(() => {
    setChatVisible(true);
    setIsOpen(true);
    setIsMinimized(false);
    if (messages.length === 0 && !initialInfoSubmitted) {
      setMessages([{
        id: 'init',
        message: 'How may I help you?',
        senderType: 'admin',
        senderName: 'AI Assistant',
        createdAt: new Date().toISOString(),
      }]);
    }
    setTimeout(() => scrollToBottom(), 300);
  }, [messages.length, initialInfoSubmitted]);

  useEffect(() => {
    window.addEventListener(MYCARSLOT_OPEN_CHAT_EVENT, handleOpenChat);
    return () => window.removeEventListener(MYCARSLOT_OPEN_CHAT_EVENT, handleOpenChat);
  }, [handleOpenChat]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !sessionId) return;

    setLoading(true);
    try {
      const payload = {
        sessionId,
        message: newMessage.trim(),
        senderType: 'user',
        userId: isAuthenticated && user ? user.id : null,
      };

      const response = await axios.post(`${API_URL}/api/chat/send`, payload);
      
      if (response.data.success) {
        // Ensure the chat message has a unique ID
        const userChat = {
          ...response.data.data.chat,
          id: response.data.data.chat.id || `chat-${Date.now()}-${Math.random()}`,
        };
        
        setMessages(prev => {
          // Check if message already exists to prevent duplicates
          const exists = prev.some(msg => msg.id === userChat.id);
          if (exists) return prev;
          return [...prev, userChat];
        });
        
        // If AI response with actions, add it to messages
        if (response.data.data.aiResponse) {
          const aiChat = {
            ...response.data.data.aiResponse,
            id: response.data.data.aiResponse.id || `ai-${Date.now()}-${Math.random()}`,
            metadata: response.data.data.aiResponse.metadata ? JSON.parse(response.data.data.aiResponse.metadata) : null,
            actions: response.data.data.actions || [],
          };
          
          setMessages(prev => {
            // Check if message already exists to prevent duplicates
            const exists = prev.some(msg => msg.id === aiChat.id);
            if (exists) return prev;
            return [...prev, aiChat];
          });
        } else {
        }
        
        // If requires human, show form to collect info
        if (response.data.data.requiresHuman) {
          setRequestingHuman(true);
          setShowInitialForm(true);
          setCurrentQuestion(0);
          // Pre-fill if user is logged in
          if (isAuthenticated && user) {
            setFormData({
              customerName: user.fullName || '',
              customerEmail: user.email || '',
              customerPhone: user.phone || '',
              concern: '',
            });
            // Skip to concern if name and email are available
            if (user.fullName && user.email) {
              setCurrentQuestion(3); // Start with concern question
            }
          }
          // Add first question
          const firstQuestion = HUMAN_SUPPORT_QUESTIONS[0];
          const botMessage = {
            id: `bot-${Date.now()}`,
            message: firstQuestion.text,
            senderType: 'admin',
            senderName: 'Support',
            createdAt: new Date().toISOString(),
          };
          setMessages(prev => [...prev, botMessage]);
          scrollToBottom();
        }
        
        setNewMessage('');
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleActionClick = async (action, carId = null, carStockNumber = null) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API_URL}/api/chat/action`, {
        withCredentials: true,
        sessionId,
        action,
        userId: isAuthenticated && user ? user.id : null,
        carId,
        carStockNumber,
      });

      if (response.data.success) {
        // Add action response to messages
        const actionMessage = {
          ...response.data.data.chat,
          id: response.data.data.chat.id || `action-${Date.now()}-${Math.random()}`,
          metadata: response.data.data.chat.metadata ? JSON.parse(response.data.data.chat.metadata) : null,
        };
        setMessages(prev => {
          // Check if message already exists to prevent duplicates
          const exists = prev.some(msg => msg.id === actionMessage.id);
          if (exists) return prev;
          return [...prev, actionMessage];
        });
        
        // If requires human, show form to collect info
        if (response.data.data.requiresHuman) {
          setRequestingHuman(true);
          setShowInitialForm(true);
          setCurrentQuestion(0);
          // Pre-fill if user is logged in
          if (isAuthenticated && user) {
            setFormData({
              customerName: user.fullName || '',
              customerEmail: user.email || '',
              customerPhone: user.phone || '',
              concern: '',
            });
            // Skip to concern if name and email are available
            if (user.fullName && user.email) {
              setCurrentQuestion(3); // Start with concern question
            }
          }
          // Add first question
          const firstQuestion = HUMAN_SUPPORT_QUESTIONS[0];
          const botMessage = {
            id: `bot-${Date.now()}`,
            message: firstQuestion.text,
            senderType: 'admin',
            senderName: 'Support',
            createdAt: new Date().toISOString(),
          };
          setMessages(prev => [...prev, botMessage]);
          scrollToBottom();
        }
        
        // If redirect URL, navigate after a short delay
        if (response.data.data.redirectUrl) {
          setTimeout(() => {
            navigate(response.data.data.redirectUrl);
            setIsOpen(false);
          }, 1500);
        }
        
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error handling action:', error);
      toast.error('Failed to process action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseChat = async () => {
    // Archive chat history in backend
    if (sessionId && initialInfoSubmitted) {
      try {
        await axios.post(`${API_URL}/api/chat/archive`, {
        withCredentials: true,
          sessionId,
          userId: isAuthenticated && user ? user.id : null,
        });
      } catch (error) {
        console.error('Error archiving chat history:', error);
        // Continue with cleanup even if archive fails
      }
    }

    // Clear localStorage
    localStorage.removeItem('chatSessionId');
    localStorage.removeItem('chatInitialInfoSubmitted');

    // Reset all state
    setIsOpen(false);
    setIsMinimized(false);
    setShowInitialForm(false);
    setInitialInfoSubmitted(false);
    setCurrentQuestion(0);
    setMessages([]);
    setNewMessage('');
    setFormData({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      concern: '',
    });
    setFormErrors({});

    // Generate new session ID for next time
    const newSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('chatSessionId', newSession);
    setSessionId(newSession);
  };

  if (!chatVisible) return null;

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button 
          className="chat-button"
          onClick={handleOpenChat}
          aria-label="Open chat"
        >
          <MessageCircle size={24} />
          <span className="chat-button-badge">Chat</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`chat-window ${isMinimized ? 'minimized' : ''}`}>
          {/* Chat Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <MessageCircle size={20} />
              <span>Live Chat Support</span>
            </div>
            <div className="chat-header-actions">
              <button
                className="chat-header-btn"
                onClick={() => setIsMinimized(!isMinimized)}
                aria-label={isMinimized ? 'Maximize' : 'Minimize'}
              >
                <Minimize2 size={18} />
              </button>
              <button
                className="chat-header-btn"
                onClick={handleCloseChat}
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <>
              {showInitialForm && !initialInfoSubmitted ? (
                <>
                  <div className="chat-messages" ref={messagesContainerRef}>
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`chat-message ${msg.senderType === 'user' ? 'user-message' : 'admin-message'}`}
                      >
                        <div className="chat-message-content">
                          <div className="chat-message-header">
                            <span className="chat-message-sender">
                              {msg.senderType === 'user' 
                                ? (msg.senderName || 'You')
                                : (msg.senderName || 'Support Team')
                              }
                            </span>
                            <span className="chat-message-time">
                              {formatTime(msg.createdAt)}
                            </span>
                          </div>
                          <div className="chat-message-text">{msg.message}</div>
                        </div>
                      </div>
                    ))}
                    {formErrors[HUMAN_SUPPORT_QUESTIONS[currentQuestion]?.key] && (
                      <div className="chat-message admin-message">
                        <div className="chat-message-content">
                          <div className="chat-message-text error">
                            {formErrors[HUMAN_SUPPORT_QUESTIONS[currentQuestion].key]}
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  <form onSubmit={handleAnswerSubmit} className="chat-input-form">
                    <input
                      type="text"
                      className="chat-input"
                      placeholder={
                        submittingForm 
                          ? 'Submitting...' 
                          : HUMAN_SUPPORT_QUESTIONS[currentQuestion]?.required 
                            ? 'Type your answer...' 
                            : 'Type your answer or press Enter to skip...'
                      }
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={submittingForm}
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="chat-send-btn"
                      disabled={submittingForm}
                    >
                      <Send size={18} />
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <div className="chat-messages" ref={messagesContainerRef}>
                    {messages.length === 0 ? (
                      <div className="chat-empty">
                        <MessageCircle size={48} />
                        <p>Start a conversation!</p>
                        <p className="chat-empty-subtitle">We're here to help you find your perfect car.</p>
                      </div>
                    ) : (
                      messages
                        .filter(msg => msg.senderName !== 'System') // Filter out system messages
                        .map((msg) => {
                          const metadata = msg.metadata || (typeof msg.metadata === 'string' ? JSON.parse(msg.metadata) : null);
                          const actions = msg.actions || metadata?.actions || [];
                          
                          return (
                            <div
                              key={msg.id}
                              className={`chat-message ${msg.senderType === 'user' ? 'user-message' : 'admin-message'}`}
                            >
                              <div className="chat-message-content">
                                <div className="chat-message-header">
                                  <span className="chat-message-sender">
                                    {msg.senderType === 'user' 
                                      ? (msg.senderName || 'You')
                                      : (msg.senderName || 'Support Team')
                                    }
                                  </span>
                                  <span className="chat-message-time">
                                    {formatTime(msg.createdAt)}
                                  </span>
                                </div>
                                <ChatMessageText text={msg.message} />
                                
                                {/* Action Buttons */}
                                {actions.length > 0 && (
                                  <div className="chat-message-actions">
                                    {actions.map((action) => (
                                      <button
                                        key={action.id}
                                        className="chat-action-button"
                                        onClick={() => handleActionClick(action.action)}
                                        disabled={loading}
                                      >
                                        {action.label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Chat Input */}
                  <form className="chat-input-form" onSubmit={sendMessage}>
                    {requestingHuman && (
                      <div className="chat-human-request-notice">
                        👤 A sales representative will be with you shortly!
                      </div>
                    )}
                    <input
                      type="text"
                      className="chat-input"
                      placeholder={requestingHuman ? "A team member will respond soon..." : "Type your message..."}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={loading || requestingHuman || showInitialForm}
                    />
                    <button
                      type="submit"
                      className="chat-send-btn"
                      disabled={loading || !newMessage.trim() || showInitialForm}
                    >
                      <Send size={18} />
                    </button>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ChatBox;

